import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Tooltip,
  keyframes,
  useColorModeValue,
  Grid,
} from '@chakra-ui/react';
import {
  FaTrophy,
  FaBolt,
  FaChartLine,
  FaStar,
  FaComments,
  FaGlobe,
  FaCrown,
  FaMedal,
  FaAward,
  FaRocket,
  FaFire,
  FaArrowUp,
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiTrendingUp } from 'react-icons/hi';
import { motion } from 'framer-motion';
import { getLocalyticsScores } from '../services/localyticsScoreService';
import { logger } from '../utils/logger';
import { useWeights } from '../contexts/WeightsContext';

const MotionBox = motion(Box);

// Keyframe animations
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.5); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

/**
 * Get a position-based quip based on the business's market rank
 * @param {number} rank - The business's rank in the market
 * @param {number} total - Total number of businesses in the market
 * @returns {string} A motivational quip based on position
 */
const getPositionQuip = (rank, total) => {
  if (!rank || !total || total === 0) return null;
  
  const percentile = ((total - rank + 1) / total) * 100;
  
  // Exact position quips
  if (rank === 1) return "You're #1 in your market! Keep dominating! 👑";
  if (rank === 2) return "Silver medal position — you're right on the leader's heels! 🥈";
  if (rank === 3) return "Bronze medal territory — the podium suits you! 🥉";
  
  // Percentile-based quips
  if (percentile >= 95) return "Elite performer! You're in the top 5% of your market! 🔥";
  if (percentile >= 90) return "You're crushing it — top 10% in your market! ⚡";
  if (percentile >= 80) return "Strong position — top 20% and climbing! ✨";
  if (percentile >= 70) return "Solid performance — you're ahead of 70% of competitors! 📈";
  if (percentile >= 60) return "Above average! You're outperforming most of your market. 💪";
  if (percentile >= 50) return "Right in the mix — a few wins could push you into the top tier! 🎯";
  if (percentile >= 40) return "Room to grow! Focus on reviews to climb the rankings. 🌱";
  if (percentile >= 30) return "Building momentum — consistent effort will move you up! 🚀";
  if (percentile >= 20) return "Every review counts! Small improvements add up fast. 📊";
  
  return "Great opportunity ahead — start building your review presence! 🌟";
};

// Score tier configuration with vibrant colors
const SCORE_TIERS = [
  { 
    min: 85, max: 100, 
    label: 'Excellent', 
    emoji: '⚡',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
    color: '#6366F1',
    bgGlow: 'rgba(99, 102, 241, 0.15)',
    description: 'Top performer in your market'
  },
  { 
    min: 70, max: 84, 
    label: 'Good', 
    emoji: '✅',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
    color: '#3B82F6',
    bgGlow: 'rgba(59, 130, 246, 0.15)',
    description: 'Solid competitive position'
  },
  { 
    min: 55, max: 69, 
    label: 'Fair', 
    emoji: '📊',
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #3B82F6 100%)',
    color: '#0EA5E9',
    bgGlow: 'rgba(14, 165, 233, 0.15)',
    description: 'Room to grow'
  },
  { 
    min: 40, max: 54, 
    label: 'Below Average', 
    emoji: '📉',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)',
    color: '#F59E0B',
    bgGlow: 'rgba(245, 158, 11, 0.15)',
    description: 'Significant room for improvement'
  },
  { 
    min: 25, max: 39, 
    label: 'Poor', 
    emoji: '⚠️',
    gradient: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
    color: '#F97316',
    bgGlow: 'rgba(249, 115, 22, 0.15)',
    description: 'Needs focused effort'
  },
  { 
    min: 0, max: 24, 
    label: 'Needs Improvement', 
    emoji: '🔧',
    gradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    color: '#64748B',
    bgGlow: 'rgba(100, 116, 139, 0.15)',
    description: 'Start with the fundamentals'
  },
];

const getScoreTier = (score) => {
  return SCORE_TIERS.find(tier => score >= tier.min && score <= tier.max) || SCORE_TIERS[SCORE_TIERS.length - 1];
};

// Normalize business name for matching
const normalizeBusinessName = (name) => {
  if (!name) return '';
  return name.toLowerCase().trim().replace(/['']/g, "'").replace(/\s+/g, ' ');
};

// Format number helper
const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

// Calculate traffic score as percentage of #1 business
const calculateTrafficScore = (monthlyVisitors, maxTraffic) => {
  if (!monthlyVisitors || monthlyVisitors <= 0 || !maxTraffic || maxTraffic <= 0) return 0;
  const score = (monthlyVisitors / maxTraffic) * 100;
  return Math.min(100, Math.max(0, Math.round(score)));
};

// Animated Score Ring Component
const AnimatedScoreRing = ({ score, tier, isAnimating }) => {
  const size = 140;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Arc from bottom-left to bottom-right (270 degrees)
  const arcLength = (270 / 360) * circumference;
  const progressLength = (score / 100) * arcLength;
  const dashOffset = arcLength - progressLength;
  
  const gradientId = `scoreGradient-${Math.random().toString(36).substr(2, 9)}`;
  const glowId = `scoreGlow-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`}>
      {/* Glow effect behind */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        w="80px"
        h="80px"
        borderRadius="full"
        bg={tier.gradient}
        filter="blur(25px)"
        opacity={0.4}
      />
      
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(135deg)' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={tier.color} stopOpacity="1" />
            <stop offset="100%" stopColor={tier.color} stopOpacity="0.6" />
          </linearGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
          opacity="0.4"
        />
        
        {/* Progress arc */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
          initial={{ strokeDashoffset: arcLength }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
        />
      </svg>
      
      {/* Center content - hardcoded position */}
      <Flex
        position="absolute"
        top="80px"
        left="70px"
        transform="translate(-50%, -50%)"
        direction="column"
        justify="center"
        align="center"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Text
            fontSize="42px"
            fontWeight="800"
            lineHeight="1"
            bgGradient={tier.gradient}
            bgClip="text"
            fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            letterSpacing="-2px"
          >
            {score}
          </Text>
        </motion.div>
        <Text
          fontSize="11px"
          fontWeight="600"
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="1px"
          mt={1}
        >
          Score
        </Text>
      </Flex>
    </Box>
  );
};

// Rank Badge Component
const RankBadge = ({ rank, total }) => {
  const isTopThree = rank && rank <= 3;
  
  const rankConfig = {
    1: { 
      icon: FaCrown, 
      label: '1st', 
      gradient: 'linear-gradient(135deg, #FCD34D 0%, #FBBF24 50%, #F59E0B 100%)',
      shadowColor: 'rgba(251, 191, 36, 0.4)',
      emoji: '👑'
    },
    2: { 
      icon: FaMedal, 
      label: '2nd', 
      gradient: 'linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 50%, #94A3B8 100%)',
      shadowColor: 'rgba(148, 163, 184, 0.4)',
      emoji: '🥈'
    },
    3: { 
      icon: FaAward, 
      label: '3rd', 
      gradient: 'linear-gradient(135deg, #FDBA74 0%, #FB923C 50%, #EA580C 100%)',
      shadowColor: 'rgba(251, 146, 60, 0.4)',
      emoji: '🥉'
    },
  };

  if (!isTopThree) {
    return (
      <Flex align="center" gap={1}>
        <Text fontSize="12px" color="gray.500" fontWeight="500">
          Ranked
        </Text>
        <Text fontSize="12px" fontWeight="700" color="gray.700">
          #{rank}
        </Text>
        <Text fontSize="12px" color="gray.400">
          of {total}
        </Text>
      </Flex>
    );
  }

  const config = rankConfig[rank];
  
  return (
    <MotionBox
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: "spring", duration: 0.8, delay: 0.3 }}
    >
      <Flex
        align="center"
        gap={2}
        bg={config.gradient}
        px={3}
        py={1.5}
        borderRadius="full"
        boxShadow={`0 4px 15px ${config.shadowColor}`}
        animation={rank === 1 ? `${float} 2s ease-in-out infinite` : undefined}
      >
        <Text fontSize="16px">{config.emoji}</Text>
        <Text
          fontSize="13px"
          fontWeight="700"
          color={rank === 2 ? 'gray.700' : 'white'}
          textShadow={rank === 2 ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'}
        >
          {config.label} Place
        </Text>
      </Flex>
    </MotionBox>
  );
};

// Metric Row Component with animated bar (original version)
const MetricRow = ({ label, icon, score, rawValue, tier, delay = 0 }) => {
  const barBg = useColorModeValue('gray.100', 'gray.700');
  
  return (
    <MotionBox
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay }}
      w="100%"
    >
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={2}>
          <Flex
            w="22px"
            h="22px"
            justify="center"
            align="center"
            borderRadius="6px"
            bg={`${tier.color}15`}
          >
            <Icon as={icon} boxSize="11px" color={tier.color} />
          </Flex>
          <Text fontSize="12px" fontWeight="600" color="gray.600">
            {label}
          </Text>
        </HStack>
        <HStack spacing={2}>
          {rawValue && (
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              {rawValue}
            </Text>
          )}
          <Text 
            fontSize="13px" 
            fontWeight="700" 
            bgGradient={tier.gradient}
            bgClip="text"
          >
            {score}
          </Text>
        </HStack>
      </Flex>
      <Box
        h="6px"
        bg={barBg}
        borderRadius="full"
        overflow="hidden"
        position="relative"
      >
        {/* Animated fill */}
        <MotionBox
          h="100%"
          bg={tier.gradient}
          borderRadius="full"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(score, 100)}%` }}
          transition={{ duration: 0.8, delay: delay + 0.2, ease: "easeOut" }}
          position="relative"
          overflow="hidden"
        >
          {/* Shimmer effect on high scores */}
          {score >= 70 && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              background="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)"
              backgroundSize="200% 100%"
              animation={`${shimmer} 2s infinite`}
            />
          )}
        </MotionBox>
      </Box>
    </MotionBox>
  );
};

// Compact Metric Item Component - Matches the new design
const CompactMetricItem = ({ name, value }) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(value);
    }, 100);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Box
      p={3}
      bg="#f9fafb"
      borderRadius="10px"
      transition="all 0.2s"
      cursor="pointer"
      _hover={{ bg: '#f3f4f6' }}
    >
      <Flex direction="column" gap="6px">
        <Flex justify="space-between" align="center">
          <Text fontSize="13px" fontWeight="600" color="#475569">
            {name}
          </Text>
          <Text fontSize="14px" fontWeight="700" color="#2563eb">
            {value}
          </Text>
        </Flex>
        <Box
          h="6px"
          bg="#e2e8f0"
          borderRadius="3px"
          overflow="hidden"
        >
          <Box
            h="100%"
            bg="linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)"
            borderRadius="3px"
            width={`${animatedValue}%`}
            transition="width 0.6s ease"
          />
        </Box>
      </Flex>
    </Box>
  );
};

// Compact Score Circle Component - Matches the new design
const CompactScoreCircle = ({ score, rank, total }) => {
  const getRankLabel = (rank) => {
    if (rank === 1) return '1ST PLACE';
    if (rank === 2) return '2ND PLACE';
    if (rank === 3) return '3RD PLACE';
    return `#${rank} OF ${total}`;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '👑';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🏆';
    return '📊';
  };

  const isTopThree = rank && rank <= 3;

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
    >
      <Box
        w="140px"
        h="140px"
        borderRadius="50%"
        background="linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        boxShadow="0 4px 16px rgba(37, 99, 235, 0.12)"
      >
        <Box
          as="span"
          fontSize="56px"
          fontWeight="800"
          color="#2563eb"
          lineHeight="1"
        >
          {score}
        </Box>
        <Box
          as="span"
          fontSize="11px"
          fontWeight="600"
          color="#64748b"
          textTransform="uppercase"
          letterSpacing="0.5px"
          marginTop="4px"
        >
          SCORE
        </Box>
      </Box>
      
      {/* Rank Badge - positioned outside the circle container */}
      {rank && total && (
        <Box
          position="absolute"
          bottom="-8px"
          left="50%"
          transform="translateX(-50%)"
          background={isTopThree ? "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" : "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"}
          color="white"
          paddingX="12px"
          paddingY="4px"
          borderRadius="12px"
          fontSize="11px"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.3px"
          boxShadow={isTopThree ? "0 2px 8px rgba(251, 191, 36, 0.4)" : "0 2px 8px rgba(100, 116, 139, 0.3)"}
          whiteSpace="nowrap"
        >
          {getRankBadge(rank)} {getRankLabel(rank)}
        </Box>
      )}
    </Box>
  );
};

// Recommendation Callout Component - Updated to use position-based quips
const RecommendationCallout = ({ recommendation, tier, rank, total }) => {
  // Get position-based quip as primary message
  const positionQuip = getPositionQuip(rank, total);
  
  // Use position quip if available, otherwise fall back to API recommendation
  const displayMessage = positionQuip || recommendation;
  
  if (!displayMessage) return null;
  
  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.8 }}
      mt={3}
      p={3}
      bg={tier.bgGlow}
      borderRadius="12px"
      border="1px solid"
      borderColor={`${tier.color}20`}
      position="relative"
      overflow="hidden"
    >
      {/* Subtle gradient overlay */}
      <Box
        position="absolute"
        top={0}
        right={0}
        w="100px"
        h="100%"
        bgGradient={`linear(to-l, ${tier.bgGlow}, transparent)`}
        pointerEvents="none"
      />
      
      <HStack spacing={2} align="flex-start">
        <Flex
          w="28px"
          h="28px"
          justify="center"
          align="center"
          borderRadius="8px"
          bg={tier.gradient}
          flexShrink={0}
          boxShadow={`0 4px 12px ${tier.color}30`}
        >
          <Icon as={HiLightningBolt} boxSize="14px" color="white" />
        </Flex>
        <VStack spacing={0.5} align="start" flex={1}>
          <Text 
            fontSize="10px" 
            fontWeight="700" 
            color={tier.color} 
            textTransform="uppercase" 
            letterSpacing="0.8px"
          >
            {positionQuip ? 'Market Position' : 'Pro Tip'}
          </Text>
          <Tooltip label={displayMessage} placement="top" hasArrow>
            <Text 
              fontSize="12px" 
              color="gray.600" 
              noOfLines={2} 
              fontWeight="500"
              lineHeight="1.4"
            >
              {displayMessage}
            </Text>
          </Tooltip>
        </VStack>
      </HStack>
    </MotionBox>
  );
};

/**
 * LocalyticsScoreCard - A stunning, modern score display card
 * Designed to be visually engaging and informative
 */
const LocalyticsScoreCard = ({ businessName, businessData, allBusinesses, rank, totalBusinesses }) => {
  const [scoreData, setScoreData] = useState(null);
  const [originalResults, setOriginalResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [computedRank, setComputedRank] = useState(null);
  const [computedTotal, setComputedTotal] = useState(null);

  // Get custom weights from context
  const { weights, isUsingCustomWeights, recalculateScoresWithWeights } = useWeights();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const actualMonthlyVisitors = businessData?.traffic_data?.metrics?.estimated_monthly_visitors || 0;

  const maxTraffic = useMemo(() => {
    if (!allBusinesses || allBusinesses.length === 0) {
      logger.log('LocalyticsScoreCard: No allBusinesses provided');
      return actualMonthlyVisitors;
    }
    
    const trafficValues = allBusinesses
      .map(b => b?.traffic_data?.metrics?.estimated_monthly_visitors || 0)
      .filter(v => v > 0);
    
    if (trafficValues.length === 0) {
      logger.log('LocalyticsScoreCard: No businesses with traffic data found');
      return actualMonthlyVisitors;
    }
    
    const max = Math.max(...trafficValues);
    logger.log(`LocalyticsScoreCard: Max traffic = ${max}, This business = ${actualMonthlyVisitors}`);
    return max;
  }, [allBusinesses, actualMonthlyVisitors]);

  useEffect(() => {
    let mounted = true;

    const fetchScore = async () => {
      if (!businessName) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getLocalyticsScores();
        
        if (mounted && response?.localytics_results?.length > 0) {
          // Store original results for recalculation with custom weights
          setOriginalResults(response.localytics_results);
          
          const normalizedSearchName = normalizeBusinessName(businessName);
          
          let matchedBusiness = response.localytics_results.find(
            b => normalizeBusinessName(b.business_name) === normalizedSearchName
          );
          
          if (!matchedBusiness) {
            matchedBusiness = response.localytics_results.find(
              b => normalizeBusinessName(b.business_name).includes(normalizedSearchName) ||
                   normalizedSearchName.includes(normalizeBusinessName(b.business_name))
            );
          }
          
          if (matchedBusiness) {
            setScoreData(matchedBusiness);
            
            const sortedResults = [...response.localytics_results].sort(
              (a, b) => b.localytics_score - a.localytics_score
            );
            const foundRank = sortedResults.findIndex(
              b => normalizeBusinessName(b.business_name) === normalizeBusinessName(matchedBusiness.business_name)
            ) + 1;
            
            setComputedRank(foundRank || null);
            setComputedTotal(response.localytics_results.length);
          } else {
            logger.error('Business not found in Localytics results:', businessName);
            setError('Business not found');
          }
        }
      } catch (err) {
        logger.error('Error fetching Localytics score:', err);
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchScore();

    return () => {
      mounted = false;
    };
  }, [businessName]);

  // Recalculate scores when weights change
  useEffect(() => {
    if (!originalResults || !businessName) return;
    
    const normalizedSearchName = normalizeBusinessName(businessName);
    
    // Get effective results (recalculated if using custom weights)
    const effectiveResults = isUsingCustomWeights 
      ? recalculateScoresWithWeights(originalResults, weights)
      : originalResults;
    
    // Find the matched business in the recalculated results
    let matchedBusiness = effectiveResults.find(
      b => normalizeBusinessName(b.business_name) === normalizedSearchName
    );
    
    if (!matchedBusiness) {
      matchedBusiness = effectiveResults.find(
        b => normalizeBusinessName(b.business_name).includes(normalizedSearchName) ||
             normalizedSearchName.includes(normalizeBusinessName(b.business_name))
      );
    }
    
    if (matchedBusiness) {
      setScoreData(matchedBusiness);
      
      // Recalculate rank based on recalculated scores
      const sortedResults = [...effectiveResults].sort(
        (a, b) => b.localytics_score - a.localytics_score
      );
      const foundRank = sortedResults.findIndex(
        b => normalizeBusinessName(b.business_name) === normalizeBusinessName(matchedBusiness.business_name)
      ) + 1;
      
      setComputedRank(foundRank || null);
      setComputedTotal(effectiveResults.length);
    }
  }, [originalResults, businessName, weights, isUsingCustomWeights, recalculateScoresWithWeights]);

  const displayRank = rank || computedRank;
  const displayTotal = totalBusinesses || computedTotal;

  // Loading state with elegant spinner
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderRadius="16px"
        p={6}
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
      >
        <Flex justify="center" align="center" minH="150px">
          <VStack spacing={4}>
            <Box position="relative">
              <Spinner 
                size="lg" 
                color="blue.500" 
                thickness="3px" 
                speed="0.8s"
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <Icon as={HiSparkles} color="blue.300" boxSize={4} />
              </Box>
            </Box>
            <Text fontSize="13px" color="gray.400" fontWeight="500">
              Calculating your score...
            </Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  // Error state
  if (error || !scoreData) {
    return (
      <Box
        bg={cardBg}
        borderRadius="16px"
        p={6}
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
      >
        <VStack spacing={3} py={6}>
          <Box
            p={3}
            borderRadius="full"
            bg="gray.50"
          >
            <Icon as={FaTrophy} boxSize={6} color="gray.300" />
          </Box>
          <Text fontSize="13px" color="gray.400" fontWeight="500">
            Score unavailable
          </Text>
        </VStack>
      </Box>
    );
  }

  const score = scoreData.localytics_score;
  const tier = getScoreTier(score);
  const isTopPerformer = displayRank && displayRank <= 3;
  const trafficScore = calculateTrafficScore(actualMonthlyVisitors, maxTraffic);

  // Metrics for the compact 2x2 grid
  const compactMetrics = [
    {
      name: 'Review Growth',
      value: scoreData.components?.recent_reviews_score || 0,
    },
    {
      name: 'Customer Sentiment',
      value: scoreData.components?.sentiment_score || 0,
    },
    {
      name: 'Market Presence',
      value: scoreData.components?.total_reviews_score || 0,
    },
    {
      name: 'Web Traffic',
      value: trafficScore,
    },
  ];

  // New Compact Design matching the provided image
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Grid
        templateColumns={{ base: "1fr", md: "140px 1fr" }}
        gap={8}
        alignItems="center"
        bg="white"
        borderRadius="16px"
        p={6}
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
      >
        {/* Left: Score Circle with Badge */}
        <CompactScoreCircle 
          score={score} 
          rank={displayRank} 
          total={displayTotal} 
        />

        {/* Right: Metrics Grid */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          {compactMetrics.map((metric, index) => (
            <CompactMetricItem 
              key={index} 
              name={metric.name} 
              value={metric.value} 
            />
          ))}
        </Grid>
      </Grid>
      
      {/* Position-based Quip / Recommendation */}
      {(displayRank || (scoreData.recommendations && scoreData.recommendations.length > 0)) && (
        <RecommendationCallout 
          recommendation={scoreData.recommendations?.[0]} 
          tier={tier}
          rank={displayRank}
          total={displayTotal}
        />
      )}
    </MotionBox>
  );
};

export default LocalyticsScoreCard;
